import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import type { ExamStatus } from '@dru/types';
import { Link } from 'react-router-dom';
import { VenueCard, type Venue } from '../components/VenueCard';
import { api } from '../lib/api';
import { CLOSED_STATUSES, checkupType, isUpcoming, shortDate, weekdayDate, weekdayDateTime, workerStatus } from '../lib/labels';
import { useOfflineQuery } from '../lib/useOfflineQuery';
import { Journey } from '../components/Journey';

interface MeExam {
  id: string;
  examinationType: string;
  status: ExamStatus;
  dueDate: string | null;
  createdAt: string;
  appointments: Array<{
    id: string; scheduledFor: string; venueType: string; status: string;
    venue: Venue | null;
  }>;
}


export function Examination() {
  const exams = useOfflineQuery<MeExam[]>('examinations', () => api.get('/me/examinations'));

  const open = exams.data?.filter((e) => !CLOSED_STATUSES.includes(e.status)) ?? [];
  const past = exams.data?.filter((e) => CLOSED_STATUSES.includes(e.status)) ?? [];

  return (
    <div className="space-y-5">
      <h1 className="text-base font-semibold text-neutral-900">Your checkups</h1>

      {exams.loading && (
        <div role="status" aria-label="Loading" className="h-40 animate-pulse rounded-xl bg-neutral-100" />
      )}

      {open.map((e) => {
        // The visit still ahead, if any — otherwise the one that happened, said
        // in the past tense rather than as an instruction.
        const upcoming = e.appointments.find((a) => isUpcoming(a.scheduledFor));
        const visit = upcoming ?? e.appointments[0];
        return (
          <div key={e.id} className="rounded-xl border border-neutral-200 bg-white p-4">
            <h2 className="text-sm font-semibold text-neutral-900">{checkupType(e.examinationType)}</h2>
            <p className="mb-3 text-xs text-neutral-600">{workerStatus(e.status)}</p>
            <Journey status={e.status} />

            {visit && (
              <>
                <div className="mt-3 rounded-lg bg-neutral-50 px-3 py-2 text-sm text-neutral-800">
                  {upcoming ? 'Appointment on ' : 'Your visit was on '}
                  <span className="font-medium">
                    {upcoming ? weekdayDateTime(visit.scheduledFor) : shortDate(visit.scheduledFor)}
                  </span>
                  {upcoming && (
                    <span className="mt-0.5 block text-xs text-neutral-600">
                      Carry a photo ID. You will be checked in at the desk.
                    </span>
                  )}
                </div>
                <VenueCard venue={visit.venue} />
              </>
            )}
          </div>
        );
      })}

      {!exams.loading && !open.length && (
        <div className="rounded-xl border border-dashed border-neutral-300 px-5 py-8 text-center">
          <p className="text-sm font-medium text-neutral-800">No checkup in progress</p>
          <p className="mt-1 text-sm text-neutral-600">
            Your employer arranges these when they are due.
          </p>
        </div>
      )}

      {past.length > 0 && (
        <section>
          <h2 className="mb-2 text-sm font-semibold text-neutral-900">Earlier checkups</h2>
          <ul className="space-y-2">
            {past.map((e) => <PastCheckup key={e.id} exam={e} />)}
          </ul>
        </section>
      )}
    </div>
  );
}


/**
 * A finished checkup, openable.
 *
 * The row was a date and a status and nothing else: a worker asking "where did
 * I go last year, and what came of it" had to leave for the Reports tab and
 * guess which visit was which. Tapping it shows the visit — when, where — and
 * points at the two things that came out of it.
 */
function PastCheckup({ exam }: { exam: MeExam }) {
  const [open, setOpen] = useState(false);
  const visit = exam.appointments[0];
  return (
    <li className="rounded-lg border border-neutral-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex min-h-12 w-full items-center justify-between gap-3 px-4 py-3 text-left active:bg-neutral-50"
      >
        <span className="min-w-0">
          <span className="block text-sm font-medium text-neutral-900">{checkupType(exam.examinationType)}</span>
          <span className="block text-xs text-neutral-600">
            {workerStatus(exam.status)} · {shortDate(visit?.scheduledFor ?? exam.createdAt)}
          </span>
        </span>
        <ChevronDown
          aria-hidden
          className={`h-5 w-5 shrink-0 text-neutral-500 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="border-t border-neutral-100 px-4 py-3">
          {visit ? (
            <>
              <p className="text-sm text-neutral-700">
                {/* "You attended" was said of every past visit, including the
                    ones that were missed or cancelled. */}
                {exam.status === 'CERTIFIED' ? 'You attended on '
                  : exam.status === 'NO_SHOW_CLOSED' ? 'You missed the appointment on '
                  : 'The appointment was for '}
                {weekdayDate(visit.scheduledFor, { year: true })}.
              </p>
              <VenueCard venue={visit.venue} />
            </>
          ) : (
            <p className="text-sm text-neutral-700">No visit was recorded for this checkup.</p>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <Link to="/records" className="inline-flex min-h-11 items-center rounded-lg border border-neutral-300 px-4 text-sm font-medium text-neutral-800 active:bg-neutral-100">
              Your reports
            </Link>
            {exam.status === 'CERTIFIED' && (
              <Link to="/fitness" className="inline-flex min-h-11 items-center rounded-lg border border-neutral-300 px-4 text-sm font-medium text-neutral-800 active:bg-neutral-100">
                Fitness certificate
              </Link>
            )}
          </div>
          <p className="mt-2 text-xs text-neutral-600">
            Your reports and your certificate are kept under the Reports and Fitness tabs.
          </p>
        </div>
      )}
    </li>
  );
}
